/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/
import {CallingIdentity, LookupType, useUploadLookupMappingFile} from '@agent-assist/api-typescript-react-query-hooks';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  MenuItem,
  InputLabel,
  Select,
  TextField,
  FormControl,
  Alert,
  Button,
  Stack,
  DialogContent,
  Dialog,
  DialogTitle,
  Typography,
  DialogActions,
} from '@mui/material';
import React, {useRef, useState} from 'react';
import {LoadingSpinner} from '../../components/LoadingSpinner';
import {useUserStore} from '../../stores/userStore';

interface ConfigureMappingsProps {
  onUpload?: (file: File, mappingType: LookupType) => Promise<void>;
}

const mappingTypes = [
  {value: 'QUEUE', label: 'Queue IDs'},
  {value: 'USER', label: 'User IDs'},
  {value: 'WRAPUP', label: 'Wrapup Codes'},
];

export const ConfigureMappings: React.FC<ConfigureMappingsProps> = ({}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [mappingType, setMappingType] = useState<LookupType>('QUEUE');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {mutateAsync: uploadMapping, isLoading: isUploading} = useUploadLookupMappingFile();
  const username = useUserStore((state) => state.username);
  const identityId = useUserStore((state) => state.userId);
  const [apiMessage, setApiMessage] = useState<string>('');
  const callingIdentity = {identityId, username};

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // read and store the file content
    if (file) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === 'csv') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const csvText = e.target?.result as string;
            const lines = csvText.split('\n');
            const headers = lines[0].split(',');
            const results: Record<string, string>[] = [];
            for (let i = 1; i < lines.length; i++) {
              if (lines[i].trim() === '') continue;
              const values = lines[i].split(',');
              const row: Record<string, string> = {};
              for (let j = 0; j < headers.length; j++) {
                row[headers[j]] = values[j] || '';
              }
              results.push(row);
            }

            const csvHeaders = headers.join(',');
            const csvRows = results.map((row) => headers.map((header) => row[header]).join(','));
            const csvData = [csvHeaders, ...csvRows].join('\n');

            setFileContent(csvData);
            setSelectedFile(file);
            console.log(csvData);
            setError(null);
          } catch (err) {
            setError('Error processing CSV file');
            console.error(err);
          }
        };
        reader.onerror = () => {
          setError('Error reading file');
        };
        reader.readAsText(file);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      if (fileContent) {
        const responseMessage = await uploadMapping({
          uploadLookupMappingFileRequestContent: {
            fileContent: JSON.stringify(fileContent),
            mappingType: mappingType,
            fileName: selectedFile.name,
            callingIdentity: callingIdentity as CallingIdentity,
          },
        });
        setApiMessage(responseMessage.message!);
      } else {
        setError('File can not be empty.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsLoading(false);
      setModalOpen(false);
    }
  };

  const onCancel = () => {
    setSelectedFile(null);
    setFileContent(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setModalOpen(false);
  };

  const handleUploadModalOpen = () => {
    setModalOpen(true);
  };
  return (
    <div>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{mb: 3}}>
        <div></div>
        <Button variant={'outlined'} onClick={handleUploadModalOpen} disabled={isUploading}>
          Upload File
        </Button>
      </Stack>
      {apiMessage && (
        <Alert severity={'success'} sx={{mt: 2}}>
          {apiMessage}
        </Alert>
      )}

      <Dialog
        open={modalOpen}
        onClose={() => !isUploading && setModalOpen(false)}
        fullWidth
        maxWidth={'sm'}
        disableEscapeKeyDown={isUploading}
      >
        <DialogTitle>Upload File</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant={'body2'}>These files will be processed and used to populate the mappings.</Typography>
            <Typography variant={'body2'}>Currently the only supported file types are CSV</Typography>
            <FormControl fullWidth>
              <InputLabel>Mapping Type</InputLabel>
              <Select
                sx={{height: 56}}
                value={mappingType}
                label={'Mapping Type'}
                onChange={(e) => setMappingType(e.target.value as LookupType)}
                disabled={isLoading}
              >
                {mappingTypes.map(({value, label}) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <TextField
                id={'file-upload'}
                type={'file'}
                onChange={handleFileChange}
                inputRef={fileInputRef}
                disabled={isLoading}
                slotProps={{
                  input: {
                    endAdornment: isLoading && <LoadingSpinner size={'sm'} />,
                    inputProps: {
                      accept: '.csv',
                    },
                  },
                }}
              />
              {error && <div className={'text-red-600 text-sm mt-1'}>{error}</div>}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            variant={'outlined'}
            startIcon={<FileUploadIcon />}
            onClick={handleSubmit}
            disabled={!selectedFile}
            loading={isUploading}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
